import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react';
import { MagnifyingGlassIcon, PhoneIcon, CheckIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

const PhoneNumberSearchModal = ({ isOpen, onClose, onSelectNumber, userId }) => {
  
  const [searchValue, setSearchValue] = useState('');
  const [searchType, setSearchType] = useState('area_code'); // 'area_code' or 'city'
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedNumber, setSelectedNumber] = useState(null);
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  // Reset state when modal opens/closes
  React.useEffect(() => {
    if (!isOpen) {
      setSearchValue('');
      setSearchResults([]);
      setSelectedNumber(null);
      setShowConfirmation(false);
      setIsSearching(false);
      setIsPurchasing(false);
    }
  }, [isOpen]);

  const handleSearch = async () => {
    if (!searchValue.trim()) return;

    setIsSearching(true);
    try {
      const response = await fetch('/api/searchAvailableNumbers', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          searchType,
          searchValue: searchValue.trim()
        })
      });

      if (!response.ok) {
        throw new Error('Search failed');
      }

      const data = await response.json();
      setSearchResults(data.numbers || []);
      
      if (data.numbers.length === 0) {
        // Show no results message
        console.log('No numbers found for search criteria');
      }
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleNumberSelect = (number) => {
    setSelectedNumber(number);
    setShowConfirmation(true);
  };

  const handleConfirmPurchase = async () => {
    if (!selectedNumber || !userId) return;

    setIsPurchasing(true);
    try {
      const response = await fetch('/api/purchasePhoneNumber', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phoneNumber: selectedNumber.phone_number,
          userId: userId
        })
      });

      if (!response.ok) {
        throw new Error('Purchase failed');
      }

      const data = await response.json();
      
      // Success! Pass the data back to parent component
      onSelectNumber({
        phoneNumber: selectedNumber.phone_number,
        telnyxId: data.telnyxId,
        success: true
      });

    } catch (error) {
      console.error('Purchase error:', error);
      alert('Failed to purchase number. Please try again.');
    } finally {
      setIsPurchasing(false);
    }
  };

  const handleBack = () => {
    if (showConfirmation) {
      setShowConfirmation(false);
      setSelectedNumber(null);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <DialogBackdrop className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
      
      <div className="fixed inset-0 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <DialogPanel className="mx-auto max-w-2xl w-full">
            {/* Liquid Glass Panel */}
            <div 
              className="relative bg-black/20 backdrop-blur-2xl rounded-3xl border border-white/10 max-h-[85vh] flex flex-col"
              style={{
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 20px 40px rgba(0,0,0,0.3)'
              }}
            >
              {/* Close Button */}
              <button
                onClick={onClose}
                disabled={isPurchasing}
                className="absolute top-6 right-6 z-10 w-10 h-10 rounded-full bg-white/10 backdrop-blur border border-white/20 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/20 transition-all"
              >
                <span className="text-lg">×</span>
              </button>

              {/* Header */}
              <div className="p-6 pb-4">
                {showConfirmation ? (
                  <>
                    <div className="flex items-center gap-3 mb-2">
                      <button
                        onClick={handleBack}
                        disabled={isPurchasing}
                        className="w-8 h-8 rounded-full bg-white/10 backdrop-blur border border-white/20 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/20 transition-all"
                      >
                        <span className="text-sm">←</span>
                      </button>
                      <h2 className="text-xl font-medium text-white">
                        Confirm Selection
                      </h2>
                    </div>
                    <p className="text-white/60 text-sm">
                      Review your selected phone number
                    </p>
                  </>
                ) : (
                  <>
                    <h2 className="text-xl font-medium text-white mb-2">
                      Search Phone Numbers
                    </h2>
                    <p className="text-white/60 text-sm">
                      Enter an area code or city name to find available numbers
                    </p>
                  </>
                )}
              </div>

              {showConfirmation ? (
                /* Confirmation View */
                <div className="px-6 pb-6 flex-1">
                  <div className="bg-white/10 backdrop-blur border border-white/20 rounded-2xl p-6 mb-6">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-16 h-16 rounded-2xl bg-white/10 backdrop-blur border border-white/20 flex items-center justify-center">
                        <PhoneIcon className="w-8 h-8 text-white/80" />
                      </div>
                      <div>
                        <div className="text-xl font-semibold text-white">
                          {selectedNumber?.formatted_number}
                        </div>
                        <div className="text-white/60 text-sm">
                          {selectedNumber?.phone_number}
                        </div>
                      </div>
                    </div>
                    
                    <div className="text-center">
                      <p className="text-white/70 text-sm">
                        This number will be added to your account and ready to use.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={handleBack}
                      disabled={isPurchasing}
                      className="flex-1 px-4 py-3 rounded-xl border border-white/20 bg-white/5 backdrop-blur text-white/80 hover:bg-white/10 hover:text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Back to Search
                    </button>
                    <button
                      onClick={handleConfirmPurchase}
                      disabled={isPurchasing}
                      className="flex-1 px-4 py-3 rounded-xl bg-white/20 backdrop-blur border border-white/30 text-white font-medium hover:bg-white/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {isPurchasing ? (
                        <>
                          <div className="w-4 h-4 rounded-full border-2 border-white/20 border-t-white animate-spin"></div>
                          Adding Number...
                        </>
                      ) : (
                        <>
                          <CheckIcon className="w-5 h-5" />
                          Confirm Selection
                        </>
                      )}
                    </button>
                  </div>
                </div>
              ) : (
                /* Search View */
                <>
                  {/* Search Controls */}
                  <div className="px-6 pb-4 space-y-4">
                    {/* Search Type Toggle */}
                    <div className="flex rounded-xl bg-white/5 backdrop-blur border border-white/10 p-1">
                      <button
                        onClick={() => setSearchType('area_code')}
                        className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                          searchType === 'area_code'
                            ? 'bg-white/20 text-white border border-white/20'
                            : 'text-white/60 hover:text-white/80'
                        }`}
                      >
                        Area Code
                      </button>
                      <button
                        onClick={() => setSearchType('city')}
                        className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                          searchType === 'city'
                            ? 'bg-white/20 text-white border border-white/20'
                            : 'text-white/60 hover:text-white/80'
                        }`}
                      >
                        City
                      </button>
                    </div>

                    {/* Search Input */}
                    <div className="relative">
                      <input
                        type="text"
                        placeholder={searchType === 'area_code' ? 'Enter area code (e.g., 212)' : 'Enter city name (e.g., New York)'}
                        value={searchValue}
                        onChange={(e) => setSearchValue(e.target.value)}
                        onKeyPress={handleKeyPress}
                        className="w-full px-4 py-3 pl-12 rounded-xl bg-white/10 backdrop-blur border border-white/20 text-white placeholder-white/40 focus:border-white/40 focus:outline-none"
                      />
                      <MagnifyingGlassIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-white/40" />
                      <button
                        onClick={handleSearch}
                        disabled={!searchValue.trim() || isSearching}
                        className="absolute right-2 top-1/2 transform -translate-y-1/2 px-4 py-1.5 rounded-lg bg-white/20 backdrop-blur border border-white/30 text-white text-sm font-medium hover:bg-white/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isSearching ? (
                          <div className="w-4 h-4 rounded-full border-2 border-white/20 border-t-white animate-spin"></div>
                        ) : (
                          'Search'
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Results */}
                  <div className="px-6 pb-6 flex-1 overflow-y-auto">
                    {isSearching ? (
                      <div className="flex items-center justify-center py-12">
                        <div className="w-6 h-6 rounded-full border-2 border-white/20 border-t-white/60 animate-spin"></div>
                        <span className="ml-3 text-white/70">Searching for available numbers...</span>
                      </div>
                    ) : searchResults.length > 0 ? (
                      <div className="space-y-3">
                        <div className="text-white/70 text-sm mb-4">
                          Found {searchResults.length} available numbers
                        </div>
                        {searchResults.map((number, index) => (
                          <button
                            key={number.phone_number}
                            onClick={() => handleNumberSelect(number)}
                            className="w-full p-4 rounded-xl border backdrop-blur-sm transition-all duration-200 flex items-center justify-between bg-white/10 border-white/20 text-white hover:bg-white/20 hover:border-white/30"
                            style={{
                              animation: `slideInUp 0.4s ease-out ${index * 0.05}s both`
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.transform = 'translateY(-2px)';
                              e.target.style.boxShadow = '0 4px 20px rgba(0,0,0,0.2)';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.transform = 'translateY(0)';
                              e.target.style.boxShadow = 'none';
                            }}
                          >
                            <div className="flex items-center gap-4">
                              <div className="w-12 h-12 rounded-xl bg-white/10 backdrop-blur border border-white/20 flex items-center justify-center">
                                <PhoneIcon className="w-6 h-6 text-white/80" />
                              </div>
                              <div className="text-left">
                                <div className="font-medium text-white">
                                  {number.formatted_number}
                                </div>
                                <div className="text-sm text-white/60">
                                  {number.phone_number}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center">
                              <ChevronRightIcon className="w-5 h-5 text-white/60" />
                            </div>
                          </button>
                        ))}
                      </div>
                    ) : searchValue && !isSearching ? (
                      <div className="flex flex-col items-center justify-center py-12 text-center">
                        <div className="w-16 h-16 rounded-2xl bg-white/10 backdrop-blur border border-white/20 flex items-center justify-center mb-4">
                          <MagnifyingGlassIcon className="w-8 h-8 text-white/40" />
                        </div>
                        <div className="text-white/70 mb-2">No numbers found</div>
                        <div className="text-white/50 text-sm">
                          Try searching for a different {searchType === 'area_code' ? 'area code' : 'city'}
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center py-12 text-center">
                        <div className="w-16 h-16 rounded-2xl bg-white/10 backdrop-blur border border-white/20 flex items-center justify-center mb-4">
                          <MagnifyingGlassIcon className="w-8 h-8 text-white/40" />
                        </div>
                        <div className="text-white/70 mb-2">Search for phone numbers</div>
                        <div className="text-white/50 text-sm">
                          Enter an {searchType === 'area_code' ? 'area code' : 'city name'} to get started
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </DialogPanel>
        </div>
      </div>

      {/* Custom Animations */}
      <style>{`
        @keyframes slideInUp {
          from { 
            opacity: 0; 
            transform: translateY(20px); 
          }
          to { 
            opacity: 1; 
            transform: translateY(0); 
          }
        }
      `}</style>
    </Dialog>
  );
};

PhoneNumberSearchModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSelectNumber: PropTypes.func.isRequired,
  userId: PropTypes.string.isRequired
};

export default PhoneNumberSearchModal;
